import type { GetStaticPropsContext } from 'next';
import { snakeCase } from 'lodash-es';
import { z } from 'zod';

import { Heading, Stack, Text } from '@chakra-ui/react';
import { ArrowRightIcon } from '@udacity/chakra-uds-icons';

import { ButtonLink } from '~/components/button-link';
import { SectionCard } from '~/components/card';
import { ContentContainer } from '~/components/container';
import { Glyph } from '~/components/glyph';
import { PageCategory } from '~/features/analytics/constants';
import { trackEvent } from '~/features/analytics/track-event';
import { DEFAULT_LANGUAGE } from '~/features/internationalization/languages';
import { PageHead } from '~/features/page/components/page-head';
import { PageLayout } from '~/features/page/components/page-layout';
import { getPage } from '~/features/page/controllers/get-page';
import { Page } from '~/features/page/models/page';
import { parsePageContentData } from '~/features/page/utils/parse-page-content-data';

const pageContentDataSchema = z.object({
  pageTitle: z.string(),
  blurb: z.string(),
  glyphName: z.string(),
  buttonLabel: z.string(),
  buttonLinkUrl: z.string(),
});

type PageContentData = z.infer<typeof pageContentDataSchema>;

export default function GetStartedSuccessPage({ page, pageData }: { page: Page; pageData: PageContentData }) {
  return (
    <>
      <PageHead page={page} pageCategory={PageCategory.GetStarted} />
      <PageLayout>
        <ContentContainer>
          <SectionCard>
            <Stack spacing={'40px'}>
              <Stack spacing={'16px'}>
                {pageData.glyphName && <Glyph height={64} name={pageData.glyphName} width={64} />}
                <Stack spacing={'8px'}>
                  <Heading as='h1' size='h1'>
                    {pageData.pageTitle}
                  </Heading>
                  <Text>{pageData.blurb}</Text>
                </Stack>
              </Stack>
              {pageData.buttonLabel && pageData.buttonLinkUrl && (
                <ButtonLink
                  buttonProps={{
                    rightIcon: <ArrowRightIcon height='24px' width='24px' />,
                    size: 'icon-right',
                    width: {
                      base: '100%',
                      md: 'fit-content',
                    },
                    onClick: () =>
                      trackEvent({
                        type: 'click',
                        name: 'Get Started CTA Clicked',
                        label: `${snakeCase(pageData.buttonLabel)}_cta_clicked`,
                      }),
                  }}
                  linkProps={{
                    href: pageData.buttonLinkUrl,
                  }}
                >
                  {pageData.buttonLabel}
                </ButtonLink>
              )}
            </Stack>
          </SectionCard>
        </ContentContainer>
      </PageLayout>
    </>
  );
}

export async function getStaticProps({ locale }: GetStaticPropsContext) {
  // Stop additional locale pages except the default locale from being built
  if (locale !== DEFAULT_LANGUAGE) {
    return {
      notFound: true,
    };
  }

  const page = await getPage('/get-started/sponsor-a-scholarship-success');
  const pageData = parsePageContentData<PageContentData>(page.pageContentData, pageContentDataSchema);

  return {
    props: {
      page,
      pageData,
    },
  };
}
